/* eslint-disable prefer-rest-params */
import { Theme } from '@emotion/react';
import { ClickAway, SearchInput } from '@tecton/ComponentRedesign';
import _debounce from 'lodash/debounce';
import moment from 'moment';
import React, { FC, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { ReactComponent } from 'react-hotkeys';
import { HotkeysProvider } from 'react-hotkeys-hook';
import { useNavigate } from 'react-router';
import { useGlobalSearch } from '../../api/fcos';
import { FcoType } from '../../types/tecton_proto/metadataservice/metadata_service';
import { useUserSettings } from '../context/UserSettingsContext';

import { Workspace } from '@tecton/ComponentRedesign/SharedComponentTypes';

import styled from '@emotion/styled';
import { FCOType } from '../../core/types/fcoTypes';
import { logEvent } from '../../utils/analytics-utils';
import GlobalSearchModal from './GlobalSearchModal';
import { SearchResultGroupNames } from './SearchResultGroup';
import SearchResults from './SearchResults';
import { GlobalSearchItemProps, GlobalSearchItemTypeRecord, typeIconMap } from './GlobalSearchTypes';

interface TectonSearchModalProps {
  setShowSearchModal: React.Dispatch<React.SetStateAction<boolean>>;
  workspace: Workspace;
  SearchRef: React.RefObject<HTMLElement>;
}

interface ComponentProps {
  children: React.ReactNode;
  workspace: Workspace;
  setShowSearchModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export const hasMetCriteriaThreshold = (filterValue: string): boolean => {
  return !!(filterValue && filterValue.length > 2);
};

interface FcoDataType {
  icon: ReactComponent;
  typeUrl: string;
  themeColor: keyof Theme['FCOColors'];
}
// FCO Mapping
export const getFcoData = (type: FcoType | 'ENVIRONMENT' | undefined): FcoDataType => {
  if (type === 'ENVIRONMENT') {
    return {
      icon: typeIconMap.ENVIRONMENT,
      typeUrl: 'environments',
      themeColor: 'environment', // or whatever color you want
    };
  }
  const itemType: string = GlobalSearchItemTypeRecord[type as FcoType | 'ENVIRONMENT'];
  const icon: React.ReactNode = typeIconMap[itemType as FCOType | 'ENVIRONMENT'];

  const fcoTypeMap: Record<FcoType, FcoDataType> = {
    [FcoType.FCO_TYPE_VIRTUAL_DATA_SOURCE]: {
      icon,
      typeUrl: 'data-sources',
      themeColor: 'source',
    },
    [FcoType.FCO_TYPE_TRANSFORMATION]: {
      icon,
      typeUrl: 'transformations',
      themeColor: 'transformation',
    },
    [FcoType.FCO_TYPE_FEATURE_VIEW]: {
      icon,
      typeUrl: 'features',
      themeColor: 'featureView',
    },
    [FcoType.FCO_TYPE_FEATURE_SERVICE]: {
      icon,
      typeUrl: 'feature-services',
      themeColor: 'featureService',
    },
    [FcoType.FCO_TYPE_ENTITY]: {
      icon,
      typeUrl: 'entities',
      themeColor: 'entity',
    },
    [FcoType.FCO_TYPE_UNSPECIFIED]: {
      icon,
      typeUrl: 'features',
      themeColor: 'featureView',
    },
  };

  return fcoTypeMap[type as FcoType];
};

const SearchPlaceholder = styled.div<{ left: string; filterValue?: string; isDisabled?: boolean }>`
  position: absolute;
  left: ${({ left }) => left};
  top: 15px;
  pointer-events: none; /* Makes the overlay not clickable */
  color: ${({ theme, isDisabled }) => (isDisabled ? theme.colors.disabledText : theme.colors.subduedText)};
  font-weight: ${({ theme }) => theme.font.weight.medium};
  ${({ filterValue }) => (filterValue && filterValue.length > 0 ? 'display: none;' : '')}  }
`;

// FCO Search Input
const GlobalSearchInput = () => {
  const { setFilterValue, workspace, filterValue, itemRefs, navigateToSearchItem, focusedItem, searchResultsLoading } =
    useSearchModalContext();

  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputRef = useCallback(() => {
    // We don't debounce here since the input value never changes
    setFilterValue(inputRef?.current?.value);
  }, [setFilterValue]);

  // Input HotKey
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (itemRefs?.current[0]) {
        itemRefs.current[0].focus();
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();

      if (focusedItem) {
        navigateToSearchItem(focusedItem);
      }
    }
  };

  return (
    <>
      <SearchInput
        variant="large"
        isLoading={searchResultsLoading && hasMetCriteriaThreshold(filterValue ?? '')}
        autoFocus
        placeholder={``}
        disabled={!workspace?.name}
        value={filterValue || ''}
        onChange={handleInputRef}
        onKeyDown={handleKeyDown}
        inputRef={inputRef}
      />
      <SearchPlaceholder left="44px" isDisabled={false} filterValue={filterValue}>
        <>Search across all workspaces</>
      </SearchPlaceholder>
      <SearchPlaceholder left="248px" isDisabled={true} filterValue={filterValue}>
        <>by name, description, tag, owner or ID</>
      </SearchPlaceholder>
    </>
  );
};

export type GlobalSearchResult = { groupName: SearchResultGroupNames; results: GlobalSearchItemProps[] };

const emptySearchResults: GlobalSearchResult[] = [
  { groupName: 'Name', results: [] },
  { groupName: 'Description', results: [] },
  { groupName: 'Tag', results: [] },
  { groupName: 'Owner', results: [] },
  { groupName: 'ID', results: [] },
];

export const getGlobalSearchItemProps = (globalSearchResult: GlobalSearchResult[]): GlobalSearchItemProps[] => {
  return globalSearchResult?.flatMap((result) => result.results) ?? [];
};

// Context for search Modal
const GlobalSearchModalContext = React.createContext<{
  filterValue: string | undefined;
  setFilterValue: React.Dispatch<React.SetStateAction<string | undefined>>;
  workspace: Workspace;
  searchResults: GlobalSearchResult[];
  searchResultsLoading: boolean;
  searchResultsError: boolean;
  itemRefs: React.MutableRefObject<(HTMLAnchorElement | HTMLButtonElement | null)[]>;
  navigateToSearchItem: (fco: GlobalSearchItemProps) => void;
  focusedIndex: number;
  setFocusedIndex: React.Dispatch<React.SetStateAction<number>>;
  focusedItem: GlobalSearchItemProps | undefined;
  setFocusedItem: React.Dispatch<React.SetStateAction<GlobalSearchItemProps | undefined>>;
}>({
  filterValue: undefined,
  setFilterValue: () => {
    return;
  },
  workspace: { name: '', workspaceType: 'Live', isAccessible: false },
  searchResults: emptySearchResults,
  searchResultsLoading: false,
  searchResultsError: false,
  itemRefs: { current: [] },
  navigateToSearchItem: () => {},
  focusedIndex: 0,
  setFocusedIndex: () => {},
  focusedItem: undefined,
  setFocusedItem: () => {},
});

const debouncedSearchCriteria = _debounce((callback) => {
  callback?.apply(this);
}, 250);

const SearchModalContextProvider: React.FC<ComponentProps> = ({ children, workspace, setShowSearchModal }) => {
  const [filterValue, setFilterValue] = useState<string | undefined>(undefined);
  const [searchCriteria, setSearchCriteria] = useState<string | undefined>(undefined);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [focusedItem, setFocusedItem] = useState<GlobalSearchItemProps | undefined>(undefined);
  const { allWorkspaces, updateWorkspace } = useUserSettings();
  const itemRefs = useRef<(HTMLAnchorElement | HTMLButtonElement | null)[]>([]);
  const navigate = useNavigate();
  let requestStartTime = moment();
  let numberOfResults = 0;

  useEffect(() => {
    // debounce so we don't make a ton of calls
    debouncedSearchCriteria(() => {
      setSearchCriteria(filterValue);
    });
  }, [filterValue]);

  const {
    data: searchResults,
    isLoading: searchResultsLoading,
    isError: searchResultsError,
  } = useGlobalSearch(searchCriteria, allWorkspaces, {
    enabled: hasMetCriteriaThreshold(filterValue ?? ''),
    select: (items) => {
      // items: GlobalSearchItemProps[]
      requestStartTime = moment();
      numberOfResults = items?.length ?? 0;
      const nameResults: GlobalSearchItemProps[] = [];
      const descriptionResults: GlobalSearchItemProps[] = [];
      const tagResults: GlobalSearchItemProps[] = [];
      const ownerResults: GlobalSearchItemProps[] = [];
      const idResults: GlobalSearchItemProps[] = [];

      items?.forEach((item, index) => {
        // Compute tagFlat for tag matching
        const tagFlat = (item.tags ?? []).map((tag) => `${tag.key}:${tag.value}`).join('');

        const isIncludedInNames = filterValue && item.name && item.name.includes(filterValue);

        // name
        if (isIncludedInNames) {
          nameResults.push({ ...item, group: 'Name', tabIndex: index });
          return;
        }

        // description: If it exists in names, don't include it here
        if (filterValue && item.description && item.description.includes(filterValue)) {
          descriptionResults.push({ ...item, group: 'Description', tabIndex: index });
          return;
        }

        // tag: If it exists in names, don't include it here
        if (filterValue && tagFlat.includes(filterValue)) {
          tagResults.push({ ...item, group: 'Tag', tabIndex: index });
          return;
        }

        // owner: If it exists in names, don't include it here
        if (filterValue && item.owner && item.owner.includes(filterValue)) {
          ownerResults.push({ ...item, group: 'Owner', tabIndex: index });
          return;
        }

        // ID: If it exists in names, don't include it here
        if (filterValue && item.id && item.id.includes(filterValue)) {
          idResults.push({ ...item, group: 'ID', tabIndex: index });
          return;
        }
      });

      //Update tab index so up and down navigation works correctly
      const orderedResults = [...nameResults, ...descriptionResults, ...tagResults, ...ownerResults, ...idResults];
      orderedResults.forEach((fco, index) => {
        fco.tabIndex = index;
      });

      const globalSearchResult: GlobalSearchResult[] = [
        { groupName: 'Name', results: nameResults },
        { groupName: 'Description', results: descriptionResults },
        { groupName: 'Tag', results: tagResults },
        { groupName: 'Owner', results: ownerResults },
        { groupName: 'ID', results: idResults },
      ];
      return globalSearchResult;
    },
  });

  useEffect(() => {
    logEvent('Search', '', {
      event: `Search results`,
      description: 'User triggers search and results are served',
      numberOfResults: `${numberOfResults}`,
      loadTime: `${moment().diff(requestStartTime, 'milliseconds')}`,
    });
    // reset the selected index when search text criteria changes
    setFocusedIndex(0);
  }, [filterValue]);

  const navigateToSearchItem = (searchItem: GlobalSearchItemProps) => {
    if (searchItem?.type && searchItem?.name) {
      const uri =
        searchItem?.type === 'ENVIRONMENT'
          ? `data-platform/environments?id=${searchItem.id}`
          : `repo/${searchItem?.workspace?.name}/${getFcoData(searchItem.type).typeUrl}/${searchItem.name}/overview`;
      setShowSearchModal(false);
      logEvent('Search', '', {
        action: 'modal closed',
        description: 'User selected a search result.',
      });

      if (searchItem?.workspace?.name) {
        updateWorkspace(searchItem.workspace?.name ?? '');
      }

      navigate(uri);
    }
  };

  return (
    <GlobalSearchModalContext.Provider
      value={{
        filterValue,
        setFilterValue,
        workspace,
        searchResults: searchResults ?? emptySearchResults,
        searchResultsLoading,
        searchResultsError,
        itemRefs,
        navigateToSearchItem,
        focusedIndex,
        setFocusedIndex,
        focusedItem,
        setFocusedItem,
      }}
    >
      {children}
    </GlobalSearchModalContext.Provider>
  );
};

export const useSearchModalContext = () => {
  const context = useContext(GlobalSearchModalContext);
  if (!context) {
    throw new Error('useSearchModalContext must be used within a SearchModalContextProvider');
  }

  return context;
};

const GlobalSearch: FC<TectonSearchModalProps> = ({ setShowSearchModal, workspace, SearchRef }) => {
  const ModalRef = useRef(null);

  const closeSearchModal = () => {
    logEvent('Search', '', {
      event: 'Search abandoned',
      exitSearch: 'clicked outside modal',
    });
    setShowSearchModal((showSearchModal: boolean) => {
      return showSearchModal ? false : true;
    });
  };

  return (
    <HotkeysProvider>
      <div ref={ModalRef}>
        <GlobalSearchModal
          ref={ModalRef}
          body={
            <>
              <SearchModalContextProvider workspace={workspace} setShowSearchModal={setShowSearchModal}>
                <ClickAway onClick={closeSearchModal} excludeRefs={[SearchRef, ModalRef]} capture={true}>
                  <GlobalSearchInput />
                  <SearchResults setShowSearchModal={setShowSearchModal} workspace={workspace.name ?? ''} />
                </ClickAway>
              </SearchModalContextProvider>
            </>
          }
          onCancel={() => {
            setShowSearchModal(false);
          }}
          onConfirm={() => {
            return;
          }}
        />
      </div>
    </HotkeysProvider>
  );
};

export default GlobalSearch;
